import React, { useState } from "react";
import { Plus, Search, Edit } from "lucide-react";
import { RolePermissionTable } from "../components/roles/RolePermissionTable";
import { RoleForm } from "../components/roles/RoleForm";
import { EditRoleForm } from "../components/roles/EditRoleForm";
import { useRolesAndPermissions } from "../hooks/useRolesAndPermissions";
import {
  CreateRoleRequest,
  UpdateRoleRequest,
  Role,
} from "../api/services/roles/type";
import toast from "react-hot-toast";
import { RoleSelectionModal } from "../components/roles/RoleSelectionModel";

export const Roles: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const {
    roles,
    permissions,
    rolePermissions,
    updateRolePermissions,
    createRole,
    updateRole,
    deleteRole,
    isLoading,
    isUpdating,
    isCreating,
    isDeleting,
    isError,
    error,
    updateError,
    createError,
    deleteError,
  } = useRolesAndPermissions();

  // Debug logging for permissions
  React.useEffect(() => {
    console.log("Permissions loaded:", permissions?.length || 0, "permissions");
    if (permissions && permissions.length > 0) {
      console.log("Sample permissions:", permissions.slice(0, 3));
    }
  }, [permissions]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePermissionToggle = async (
    roleId: string,
    permissionId: string,
    hasPermission: boolean,
  ) => {
    try {
      await updateRolePermissions(roleId, permissionId, hasPermission);

      if (hasPermission) {
        toast.success("Permission granted!");
      } else {
        toast.success("Permission revoked!");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to update permission: ${message}`);
    }
  };

  const handleAddRole = () => {
    setShowRoleForm(true);
  };

  const handleEditRoles = () => {
    setShowRoleSelection(true);
  };

  const handleRoleFormSubmit = async (data: CreateRoleRequest) => {
    try {
      await createRole(data);
      toast.success("Role created successfully!");
      setShowRoleForm(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to create role: ${message}`);
    }
  };

  const handleSelectRoleForEdit = (role: Role) => {
    setSelectedRole(role);
    setShowRoleSelection(false);
    setShowEditForm(true);
  };

  const handleEditFormSubmit = async (data: UpdateRoleRequest) => {
    if (!selectedRole) return;

    try {
      await updateRole(selectedRole.id, data);
      toast.success("Role updated successfully!");
      setShowEditForm(false);
      setSelectedRole(null);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to update role: ${message}`);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    const role = roles.find((r) => r.id === roleId);
    if (role?.isSystemRole) {
      toast.error("Cannot delete system roles");
      return;
    }

    try {
      await deleteRole(roleId);
      toast.success("Role deleted successfully!");
      setShowRoleSelection(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to delete role: ${message}`);
    }
  };

  const handleRoleFormCancel = () => {
    setShowRoleForm(false);
  };

  const handleRoleSelectionCancel = () => {
    setShowRoleSelection(false);
  };

  const handleEditFormCancel = () => {
    setShowEditForm(false);
    setSelectedRole(null);
  };

  const handleMenuToggle = async (
    roleId: string,
    menuId: string,
    hasAccess: boolean,
  ) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    // Get current menu IDs from both sidebarMenuIds and sidebar_menus
    const currentMenuIds =
      role.sidebarMenuIds || role.sidebar_menus?.map((menu) => menu.id) || [];

    let updatedMenuIds: string[];
    if (hasAccess) {
      updatedMenuIds = currentMenuIds.includes(menuId)
        ? currentMenuIds
        : [...currentMenuIds, menuId];
    } else {
      updatedMenuIds = currentMenuIds.filter((id) => id !== menuId);
    }

    const updateData = {
      name: role.name,
      description: role.description,
      is_system_role: role.isSystemRole,
      permissionIds: role.permissions.map((p) => p.id),
      sidebarMenuIds: updatedMenuIds,
    };

    try {
      await updateRole(roleId, updateData);
      if (hasAccess) {
        toast.success(`Menu access granted to ${role.name}!`);
      } else {
        toast.success(`Menu access revoked from ${role.name}!`);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to update menu access: ${message}`);
    }
  };

  // Filter roles based on search term
  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Roles and Permissions
            </h3>
            <p className="text-red-300">
              {error?.message ||
                "Failed to load roles and permissions. Please try again."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search and Action Buttons */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search roles and permissions..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleEditRoles}
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap border border-slate-600"
              >
                <Edit size={20} />
                Edit Roles
              </button>
              <button
                onClick={handleAddRole}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                <Plus size={20} />
                Add Roles and Permissions
              </button>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {updateError && (
          <div className="bg-red-800/20 border border-red-600 text-red-300 px-4 py-3 rounded-lg mb-4">
            Update Error: {updateError.message}
          </div>
        )}

        {createError && (
          <div className="bg-red-800/20 border border-red-600 text-red-300 px-4 py-3 rounded-lg mb-4">
            Create Error: {createError.message}
          </div>
        )}

        {deleteError && (
          <div className="bg-red-800/20 border border-red-600 text-red-300 px-4 py-3 rounded-lg mb-4">
            Delete Error: {deleteError.message}
          </div>
        )}

        {/* Roles and Permissions Table */}
        <RolePermissionTable
          roles={filteredRoles}
          permissions={permissions}
          rolePermissions={rolePermissions}
          onPermissionToggle={handlePermissionToggle}
          onMenuToggle={handleMenuToggle}
          isLoading={isLoading}
          isUpdating={isUpdating}
        />

        {/* Stats */}
        <div className="mt-6 text-sm text-slate-400">
          Showing {filteredRoles.length} of {roles.length} roles •{" "}
          {permissions.length} total permissions
        </div>
      </div>

      {/* Modals */}
      {showRoleForm && (
        <RoleForm
          permissions={permissions}
          isLoading={isCreating}
          onSubmit={handleRoleFormSubmit}
          onCancel={handleRoleFormCancel}
        />
      )}

      {showRoleSelection && (
        <RoleSelectionModal
          roles={roles}
          onSelectRole={handleSelectRoleForEdit}
          onDeleteRole={handleDeleteRole}
          onCancel={handleRoleSelectionCancel}
          isDeleting={isDeleting}
        />
      )}

      {showEditForm && selectedRole && (
        <EditRoleForm
          role={selectedRole}
          permissions={permissions}
          isLoading={isUpdating}
          onSubmit={handleEditFormSubmit}
          onCancel={handleEditFormCancel}
        />
      )}
    </div>
  );
};
